import { AxiosError } from 'axios';
import qs from 'qs';
import { AnyAction } from 'redux';

// Data Layer
import {
  login as authLogin,
  logout as authLogout,
  getLoggedEmail,
  getLoggedRole,
  getLoggedUserId,
  getLoggedUserName,
  isLoggedIn,
} from 'data-layer-cr/dist/authentication';
import { GeneralCatalogItem, ServerResponse } from 'data-layer-cr/dist/models';
import { setRequestStatus } from 'data-layer-cr/dist/network';
import { NetworkResponse } from 'data-layer-cr/dist/network/NetworkTypes';

// Analytics
import {
  ASSANET_LOGIN,
  DIGITAL_LOGIN,
  LOGGED_ROLE,
  LOGIN_ORIGIN,
  USER_IDENTIFICATION_STEP,
  setUserProperties,
  trackCurrentStep,
  login as trackUserLogin,
} from 'src/analytics';

// Models
import Profile from '../../../models/Profile';
import LicenseResponse from '../../../models/responseTypes/LicenseResponse';

// Redux Related
import { AppThunk, AsyncAppThunk, AsyncDispatch } from '../../ThunkTypes';
import { setPricingProfile } from '../../pricingProfiles/slice/pricingProfileSlice';
import {
  overwriteCurrentQuotation,
  updateIsLicenseAutoPx,
} from '../../quotation/slice/quotationSlice';
import {
  GET_AUTO_PX,
  GET_LICENSES,
  LOGIN,
  PROFILE_REFRESH,
} from '../requestIdentifiers';
import { updateLicenses, updateProfileData } from '../slice/profileSlice';

// Network related
import {
  AuthRequest,
  AuthResponse,
} from '../../../models/responseTypes/AuthResponse';
import AuthNetworkClient from '../../../services/AuthNetworkClient';
import processRequest from '../../../services/processRequest';

export const GENERIC_ERROR = 'Error de conexión';
export const CREDENTIALS_ERROR = 'Usuario o contraseña inválidos';

/**
 * Creates a custom error message if credentials are invalid
 *
 * @param error - the error returned by the axios client
 */
export function makeLoginError(error?: AxiosError): string {
  if (error && error.response && error.response.data) {
    const code = error.response.data.error;
    if (code === 'invalid_grant') {
      return CREDENTIALS_ERROR;
    }
  }
  return GENERIC_ERROR;
}

/**
 * Register login user information into mixpanel
 *
 * @param origin
 */
function trackUserData(origin: string): void {
  const currentDate = new Date().toISOString();

  trackUserLogin(getLoggedUserId(), getLoggedUserName(), getLoggedEmail());
  setUserProperties({
    $distinct_id: getLoggedUserId(),
    ID: getLoggedUserId(),
    $name: getLoggedUserName(),
    $email: getLoggedEmail(),
    [LOGGED_ROLE]: getLoggedRole(),
    [LOGIN_ORIGIN]: origin,
  });

  trackCurrentStep(USER_IDENTIFICATION_STEP, currentDate);
}

/**
 * So, the only value needed (for now) from the previously defined profile is the
 * name, so we created a method that returns a pre-initialized profile instance with
 * just the provided name, to save us time.
 */
function profileWithFallbacks({
  name,
  id,
}: {
  name: string;
  id: string;
}): Profile {
  return {
    Name: name,
    IdUser: id,
    LastName: '',
    State: '',
    Profile: '',
    Email: '',
    Telephone: '',
  };
}

/**
 * Requests a list of licenses associated with the current user
 */
export const getLicenses =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(GET_LICENSES, 'requesting'));
    try {
      const response = await processRequest<LicenseResponse>({
        method: 'GET',
        url: 'license',
        client: AuthNetworkClient,
      });
      const parsedData = response.LicenseList.map(
        (item, index): GeneralCatalogItem => ({
          id: index,
          code: item.LICENCIA,
          name: item.NOMBRE,
        }),
      );
      dispatch(updateLicenses(parsedData));
      return dispatch(setRequestStatus(GET_LICENSES, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(GET_LICENSES, 'failure'));
    }
  };

/**
 * Consult if auto px is applicable for collaborator
 * Note that this function should only be called for collaborator roles
 */
export const getAutoPxResult =
  (licenseCode: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    dispatch(setRequestStatus(GET_AUTO_PX, 'requesting'));

    const filterOption = `?filter[where][code][eq]=${licenseCode.toUpperCase()}`;

    try {
      const response = await processRequest<
        NetworkResponse<GeneralCatalogItem[]>
      >({
        method: 'GET',
        url: `Catalog/policy/brokerlicenses${filterOption}`,
      });

      const hasAutoPx = response.data.length > 0;

      dispatch(
        overwriteCurrentQuotation({
          ...getState().quotation.currentQuotation,
          isAutoPx: hasAutoPx,
        }),
      );

      dispatch(updateIsLicenseAutoPx(hasAutoPx));

      return dispatch(setRequestStatus(GET_AUTO_PX, 'success'));
    } catch (error) {
      return dispatch(setRequestStatus(GET_AUTO_PX, 'failure'));
    }
  };

/**
 * Refreshes registered user data.
 */
export const refreshProfile =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(PROFILE_REFRESH, 'requesting'));

    try {
      const data = await processRequest<Profile>({
        method: 'GET',
        url: 'auth/profile',
        data: qs.stringify({
          grant_type: 'password',
          client_id: 'sales-web',
          client_secret: 'secret',
          scope: 'sales-journey-ms openid IdentityServerApi',
        }),
      });
      dispatch(updateProfileData(data));
      return dispatch(setRequestStatus(PROFILE_REFRESH, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(PROFILE_REFRESH, 'failure'));
    }
  };

/**
 * This method will force the app to update the profile data from the information provided
 * inside the token
 */
export const refresProfile =
  (): AppThunk =>
  (dispatch, getState): void => {
    const { profile } = getState().profile;
    if (!profile && isLoggedIn()) {
      const newProfile = profileWithFallbacks({
        name: getLoggedUserName(),
        id: getLoggedUserId(),
      });
      dispatch(updateProfileData(newProfile));
    }
  };

/**
 * Logins the user to the application
 */
export const login =
  (
    username: string,
    password: string,
    authenticationType: string,
    recaptchaToken: string,
  ): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    const payload: AuthRequest = {
      username,
      password,
      recaptchaToken,
      authenticationType,
    };
    dispatch(setRequestStatus(LOGIN, 'requesting'));
    try {
      const { data } = await processRequest<ServerResponse<AuthResponse>>({
        method: 'POST',
        url: '/authentication/user',
        data: payload,
        customErrorMessage: makeLoginError,
      });
      trackUserData(DIGITAL_LOGIN);
      authLogin({
        token: data.access_token,
        expiration: data.expires_in,
      });
      const profile = profileWithFallbacks({
        name: getLoggedUserName(),
        id: getLoggedUserId(),
      });

      dispatch(updateProfileData(profile));

      return dispatch(setRequestStatus(LOGIN, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(LOGIN, 'failure'));
    }
  };

/**
 * External Logins by hash the user to the application
 */
export const loginExternalByHash =
  (hash: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(LOGIN, 'requesting'));
    try {
      const data = await processRequest<AuthResponse>({
        method: 'POST',
        url: `connect/token?hash=${hash}`,
        data: qs.stringify({
          grant_type: 'password',
          client_id: 'sales-web',
          client_secret: 'secret',
          username: 'assanet',
          password: '',
        }),
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        customErrorMessage: makeLoginError,
        client: AuthNetworkClient,
      });

      authLogin({
        token: data.access_token,
        expiration: data.expires_in,
        externalToken: hash,
      });
      trackUserData(ASSANET_LOGIN);
      const profile = profileWithFallbacks({
        name: getLoggedUserName(),
        id: getLoggedUserId(),
      });

      dispatch(updateProfileData(profile));
      return dispatch(setRequestStatus(LOGIN, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(LOGIN, 'failure'));
    }
  };

/**
 * External Logins by token the user to the application
 */
export const loginExternalByToken =
  (token: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(LOGIN, 'requesting'));
    try {
      await processRequest<AuthResponse>({
        method: 'POST',
        url: 'connect/userinfo',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        customErrorMessage: makeLoginError,
        client: AuthNetworkClient,
      });

      authLogin({
        token,
      });

      const profile = profileWithFallbacks({
        name: getLoggedUserName(),
        id: getLoggedUserId(),
      });

      dispatch(updateProfileData(profile));

      trackUserData(ASSANET_LOGIN);

      return dispatch(setRequestStatus(LOGIN, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(LOGIN, 'failure'));
    }
  };

/**
 * Removes the current user session from the application
 */
export const logout =
  (): AppThunk =>
  (dispatch): void => {
    authLogout();
    dispatch(updateLicenses([]));
    dispatch(updateProfileData(null));
    dispatch(setPricingProfile([]));
  };
